import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSiteImportUIStore } from '../../stores/siteImportUIStore';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';
import PathSelectionStep from './steps/PathSelectionStep';
import SiteInformationStep from './steps/SiteInformationStep';
import TradeTemplatePicker from './steps/TradeTemplatePicker';
import ReviewConfirmStep from './steps/ReviewConfirmStep';
import ImportSuccessStep from './steps/ImportSuccessStep';
import SiteImportBottomToolbar from './SiteImportBottomToolbar';
import FileUploadStep from './steps/FileUploadStep';
import {
  siteImportSchema,
  SiteImportFormData,
} from '../../schemas/siteImportSchema';
import { useSiteImportFlow } from '../../hooks/useSiteImportFlow';

const stepTitles: Record<
  number,
  Record<string, { title: string; subtitle: string }>
> = {
  0: {
    default: {
      title: 'Site details',
      subtitle:
        'Give this site a name and a short code. You can also add a location and notes.',
    },
  },
  1: {
    default: {
      title: 'How would you like to set up this site?',
      subtitle:
        'Choose a starting point for your new site. You can use a ready-made trade template or upload your own CSV file.',
    },
  },
  2: {
    template: {
      title: 'Pick a trade template',
      subtitle:
        'Each template comes with example plots and labour items to get you started quickly.',
    },
    csv: {
      title: 'Upload your CSV',
      subtitle:
        "Each row should map a labour item to a plot. We'll validate and preview your data before importing.",
    },
  },
  3: {
    default: {
      title: 'Review & confirm',
      subtitle: 'Check everything looks right before we create the site.',
    },
  },
  4: {
    default: {
      title: 'Site created!',
      subtitle: 'Your site is ready to go.',
    },
  },
};

const SiteImportStepperV2: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const {
    activeStep,
    importPath,
    showCancelConfirm,
    setShowCancelConfirm,
    resetNavState,
  } = useSiteImportUIStore();

  const form = useForm<SiteImportFormData>({
    resolver: zodResolver(siteImportSchema),
    defaultValues: {
      name: '',
      slug: '',
      what3words: '',
      notes: '',
      latitude: null,
      longitude: null,
    },
    mode: 'onTouched',
  });

  const {
    selectedFile,
    previewData,
    importResult,
    templateFetchError,
    allLabourItemsResolved,
    setAllLabourItemsResolved,
    selectedTemplateId,
    uploading,
    importing,
    uploadError,
    handleSelectTemplate,
    handleTemplateConfirm,
    handleFileSelected,
    handleImport,
    handleConfirmCancel,
  } = useSiteImportFlow(form);

  const getStepLabels = () => {
    const labels = ['Site Details', 'Choose Source'];
    labels.push(importPath === 'template' ? 'Choose Template' : 'Upload CSV');
    labels.push('Review & Confirm');
    return labels;
  };

  const stepKey = activeStep === 2 ? importPath || 'csv' : 'default';
  const { title, subtitle } = stepTitles[activeStep]?.[stepKey] ||
    stepTitles[activeStep]?.['default'] || { title: '', subtitle: '' };

  const renderStepComponent = () => {
    switch (activeStep) {
      case 0:
        return <SiteInformationStep />;
      case 1:
        return <PathSelectionStep />;
      case 2:
        return importPath === 'template' ? (
          <>
            {templateFetchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {templateFetchError}
              </Alert>
            )}
            <TradeTemplatePicker
              selectedTemplateId={selectedTemplateId}
              onSelectTemplate={handleSelectTemplate}
              siteName={form.getValues('name')}
            />
          </>
        ) : (
          <FileUploadStep
            onFileSelected={handleFileSelected}
            loading={uploading}
            selectedFile={selectedFile}
            error={uploadError}
          />
        );
      case 3:
        return (
          <ReviewConfirmStep
            previewData={previewData}
            importPath={importPath}
            onAllResolved={() => setAllLabourItemsResolved(true)}
          />
        );
      case 4:
        return importResult ? (
          <ImportSuccessStep
            importResult={importResult}
            onViewSite={() => {
              navigate(`/sites/${importResult.siteId}`);
              resetNavState();
            }}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...form}>
      <Box
        sx={{ pb: 10, maxWidth: 640, mx: 'auto' }}
        data-testid="site-import-stepper"
      >
        {/* Stepper navigation — hidden on mobile and on success step */}
        {!isMobile && activeStep < 4 && (
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {getStepLabels().map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        {/* Step title + subtitle — hidden on success step */}
        {activeStep < 4 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              component="h1"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              {title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        )}

        {/* Step content */}
        {renderStepComponent()}

        {/* Bottom toolbar — hidden on success step */}
        {activeStep < 4 && (
          <SiteImportBottomToolbar
            previewData={previewData}
            selectedTemplateId={selectedTemplateId}
            allLabourItemsResolved={allLabourItemsResolved}
            uploading={uploading}
            importing={importing}
            onImport={handleImport}
            onCancel={() => setShowCancelConfirm(true)}
            onTemplateConfirm={handleTemplateConfirm}
          />
        )}

        {/* Cancel confirmation */}
        <ConfirmDialog
          open={showCancelConfirm}
          title="Cancel Import"
          message="Are you sure you want to cancel? All progress will be lost."
          onConfirm={handleConfirmCancel}
          onCancel={() => setShowCancelConfirm(false)}
          confirmText="Yes, Cancel"
          cancelText="Continue"
        />
      </Box>
    </FormProvider>
  );
};

export default SiteImportStepperV2;
